import { toast } from '@qodo/design-system';
import i18n from 'i18next';
import isNil from 'lodash/isNil';
import { v4 as uuid4 } from 'uuid';
import {
  CLIENT_TYPES,
  CLI_LIKE_CLIENTS,
  LOGIN_REQUIRED_CLIENTS,
} from '../consts/authentication';
import { LOCAL_STORAGE_KEYS } from '../consts/localStorage';
import { SESSION_STORAGE_KEYS } from '../consts/sessionStorage';
import { OIDC_AUTHENTICATION_ENDPOINTS } from '../network/httpClients/OIDCAuthenticationClient/consts';
import {
  MARKETING_DEFAULTS,
  MARKETING_PARAMS,
  QUERY_PARAMS,
  ROUTE_PATHS,
} from '../routes/consts';
import { getDynamicSetting } from './dynamicSettings';

/**
 * Time window in milliseconds to detect redirect loops
 * If the same URL is redirected to within this timeframe, it's considered a loop
 */
const REDIRECT_LOOP_TIMEOUT_MS = 5000;

const VALID_REDIRECT_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Opens a URL in a new tab or the same tab.
 *
 * Guards against empty / falsy URLs: calling `window.open('')` opens a blank
 * tab, which is a confusing UX when a caller passes a missing URL by mistake
 * (e.g. partially loaded data). In that case, surfaces a toast and bails out.
 *
 * @param url - The URL to open.
 * @param isNewTab - Whether to open the URL in a new tab.
 */
export const openUrl = (url: string, isNewTab = false) => {
  if (!url) {
    console.warn('openUrl called with an empty url. Ignoring.');
    toast.error(i18n.t('toasts.open-url.empty'));

    return undefined;
  }

  return window.open(url, isNewTab ? '_blank' : '_self', 'noreferrer');
};

/** Hard-reload the current page — forces a full re-fetch of app/billing state. */
export const reloadPage = (): void => {
  window.location.reload();
};

/**
 * Returns the path segments from the current URL
 * Empty Segments are filtered out
 * The function is NOT returning the query params
 * @param pathname - The pathname to extract segments from
 * @example
 * extractPathSegments('/home/users/123') // ['home', 'users', '123']
 * extractPathSegments('/home/users/123?id=123') // ['home', 'users', '123']
 */
export const extractPathSegments = (pathname: string): string[] => {
  if (!pathname) {
    return [];
  }

  return pathname.split('?')[0].split('/').filter(Boolean);
};

/**
 * Returns the query param from the current URL
 * @param paramName - The name of the query param to return
 * @returns {string | null} The value of the query param
 */
export const getQueryParam = (paramName: string) => {
  const searchParams = new URLSearchParams(location.search);

  return searchParams.get(paramName);
};

/**
 * Clears query parameters from the URL.
 *
 * @example
 * ```ts
 * // Before: /integrations/github/1?status=error&error_code=APP_NOT_FOUND
 * clearErrorQueryParams();
 * // After: /integrations/github/1
 * ```
 */
export const clearQueryParams = (): void => {
  window.history.replaceState({}, '', window.location.pathname);
};

/**
 * Detects if the Aware flow is active based on URL query params
 * We consider the flow "on" when method=aware is present
 */
export const isAwareFlowOn = (): boolean => {
  return getQueryParam(QUERY_PARAMS.METHOD) === 'aware';
};

/**
 * Navigates to a new pathname
 * @param newPathname - The new pathname to navigate to
 */
export const goToPathname = (
  newPathname: string,
  searchParams?: URLSearchParams,
) => {
  if (window.location.pathname === newPathname) return;

  let search = searchParams ? searchParams.toString() : window.location.search;

  // Add '?' prefix if search is not empty and doesn't already have it
  if (search && !search.startsWith('?')) {
    search = `?${search}`;
  }

  window.location.href = `${newPathname}${search}`;
};

/**
 * Checks if the current URL is the login page. login page can be either the root login page or one of the login sub pages.
 * @returns True if the current URL is a login page, false otherwise
 */
export const isLoginPage = () => {
  return extractPathSegments(window.location.pathname).includes(
    ROUTE_PATHS.SIGNIN,
  );
};

/**
 * Checks if the current URL is the success signin external client page
 * @returns True if the current URL is the success signin external client page, false otherwise
 */
export const isSuccessSigninExternalClientPage = () => {
  return extractPathSegments(window.location.pathname).includes(
    ROUTE_PATHS.SUCCESS_SIGNIN_EXTERNAL_CLIENT,
  );
};

/**
 * Checks if the current URL is the error page
 * @returns True if the current URL is the error page, false otherwise
 */
export const isErrorPage = () => {
  return extractPathSegments(window.location.pathname).includes(
    ROUTE_PATHS.ERROR,
  );
};

const BITBUCKET_OAUTH_POPUP_PREFIX = '/integrations/bitbucket/oauth';

export const isBitbucketOAuthPopupPage = () =>
  window.location.pathname.startsWith(BITBUCKET_OAUTH_POPUP_PREFIX);

/**
 * Checks if the current URL is the generic success page
 * @returns True if the current URL is the success page, false otherwise
 */
export const isSuccessPage = () => {
  return extractPathSegments(window.location.pathname).includes(
    ROUTE_PATHS.SUCCESS,
  );
};

/**
 * Checks if the current URL is an authenticated page. An authenticated page is a page that is an "in app" page, and not a login page or an error page (which are the only pages that are "outside" of the app)
 * @returns True if the current URL is an authenticated page, false otherwise
 */
export const isAuthenticatedPage = () => {
  return (
    !isLoginPage() &&
    !isErrorPage() &&
    !isSuccessPage() &&
    !isSuccessSigninExternalClientPage() &&
    !isBitbucketOAuthPopupPage()
  );
};

/**
 * Determines if the auth data should be persisted in localStorage
 * This is used to prevent the auth data from being persisted in localStorage for external clients in OIDC flow, because when an external client is logging in,
 * the auth data should not be persisted in localStorage, and the user is redirected to the success page.
 * @returns true if the auth data should be persisted in localStorage, false otherwise
 */
export const shouldPersistAuthData = (): boolean => {
  const initClient = getQueryParam(QUERY_PARAMS.INIT_CLIENT) || 'portal';
  const isSuccessPage = isSuccessSigninExternalClientPage();

  if (LOGIN_REQUIRED_CLIENTS.includes(initClient) && isSuccessPage) {
    return false;
  }

  return true;
};

/**
 * Builds a URL with the given query params. This function is persisting the current query params in the URL.
 * @param url - The URL to build
 * @param newQueryParams - The query params to add to the URL
 * @returns The URL with the query params
 */
export const buildURLWithQueryParams = (
  url: string,
  newQueryParams?: Record<string, string> | URLSearchParams,
) => {
  const urlObj = new URL(url);

  if (newQueryParams) {
    if (newQueryParams instanceof URLSearchParams) {
      newQueryParams.forEach((value, key) => {
        urlObj.searchParams.append(key, value);
      });
    } else {
      Object.entries(newQueryParams).forEach(([key, value]) => {
        urlObj.searchParams.set(key, value);
      });
    }
  }

  return urlObj.toString();
};

/**
 * Returns the OIDC authorization URL
 * @param params - Optional URLSearchParams to append to the URL
 * @returns The OIDC authorization URL with optional query parameters
 */
export const getOIDCAuthorizeEndpointUrl = (params?: URLSearchParams) => {
  const authServerUrl = getDynamicSetting('auth_url_oidc');
  const baseUrl = `${authServerUrl}/${OIDC_AUTHENTICATION_ENDPOINTS.AUTHORIZE}`;

  if (params) {
    return `${baseUrl}?${params.toString()}`;
  }

  return baseUrl;
};

/**
 * Stores an ID in session storage. If the ID is not present in the query params, it generates a new one (if the shouldGenerateId argument is true).
 * @param queryParamKey - The key of the query param, for checking if the ID is present in the query params
 * @param sessionStorageKey - The key of the session storage item to store (if not existed already)
 * @param shouldGenerateId - Whether to generate a new ID if not present in the query params
 */
export const storeIdInSessionStorage = (
  queryParamKey: string,
  sessionStorageKey: string,
  shouldGenerateId: boolean = true,
) => {
  const valueFromQueryParam = getQueryParam(queryParamKey);
  const valueFromSessionStorage = sessionStorage.getItem(sessionStorageKey);

  const isValueFromQueryParamValid =
    valueFromQueryParam && valueFromQueryParam !== 'None';

  if (isValueFromQueryParamValid) {
    sessionStorage.setItem(sessionStorageKey, valueFromQueryParam);
  } else if (!valueFromSessionStorage && shouldGenerateId) {
    const generatedId = uuid4();

    sessionStorage.setItem(sessionStorageKey, generatedId as string);
  }
};

/**
 * Gets the trace_id from session storage. If not found, it checks the query params. If not found, generates a new one.
 * @returns The trace_id value from session storage, query params, or a newly generated UUID
 */
export const getTraceIdFromSessionStorage = (): string => {
  const existingTraceId = sessionStorage.getItem(SESSION_STORAGE_KEYS.TRACE_ID);
  const queryParamTraceId = getQueryParam(QUERY_PARAMS.TRACE_ID);

  if (existingTraceId) {
    return existingTraceId;
  }

  if (queryParamTraceId) {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.TRACE_ID, queryParamTraceId);

    return queryParamTraceId;
  }

  // Generate new trace_id if not provided from backend
  const newTraceId = uuid4();

  sessionStorage.setItem(SESSION_STORAGE_KEYS.TRACE_ID, newTraceId);

  return newTraceId;
};

/** Anchored Qodo portal host: any `*.qodo.ai` subdomain, no trailing/leading segments. */
const QODO_PORTAL_HOST_PATTERN = /^[\w-]+(\.[\w-]+)*\.qodo\.ai$/;

/**
 * Whether a URL is a genuine Qodo portal redirect target: https, an anchored `*.qodo.ai`
 * host (the org's dedicatedPortalUrl), no userinfo. Rejects look-alikes
 * (`qodo.ai.evil.com`), non-https, and credentialed URLs.
 * @param url - The candidate redirect URL.
 */
const isQodoPortalHost = (url: string): boolean => {
  try {
    const parsed = new URL(url);

    return (
      parsed.protocol === 'https:' &&
      !parsed.username &&
      !parsed.password &&
      QODO_PORTAL_HOST_PATTERN.test(parsed.hostname)
    );
  } catch {
    return false;
  }
};

/** The device-login success route DeviceCodeVerify mounts on: `/signin/success-signin-external-client`. */
const SUCCESS_SIGNIN_EXTERNAL_CLIENT_PATH = `/${ROUTE_PATHS.SIGNIN}/${ROUTE_PATHS.SUCCESS_SIGNIN_EXTERNAL_CLIENT}`;

/**
 * Rewrites the MT→dedicated 409 redirect URL for a CLI device login: points it at the ST
 * success page (so DeviceCodeVerify mounts and auto-adopts) and appends the CLI device
 * context (init_client/trace_id/user_code). No-op unless a CLI login is in flight AND the
 * target is a genuine `*.qodo.ai` host — so the normal portal-user tenant-redirect (to the
 * bare dedicatedPortalUrl) is untouched.
 *
 * The backend's redirectTo is the bare dedicatedPortalUrl (an origin, no path), so without
 * the path rewrite the browser lands on the ST root → RootProtectedRoute sends it to the
 * login buttons, not the device-confirm page. Only overwrite an absent/root path so a
 * backend that ever includes the success path isn't doubled.
 * @param redirectTo - The destination URL from the 409 redirect.
 * @returns The rewritten URL for a CLI login, or the original when not applicable.
 */
export const appendCliRedirectParams = (redirectTo: string): string => {
  if (!CLI_LIKE_CLIENTS.includes(getInitClientFromSessionStorage())) {
    return redirectTo;
  }

  // Only rewrite for a genuine Qodo portal host (the org's dedicatedPortalUrl), never an
  // external origin.
  if (!isQodoPortalHost(redirectTo)) {
    return redirectTo;
  }

  // Point the redirect at the device-login success page so the ST landing mounts
  // DeviceCodeVerify. The backend's dedicatedPortalUrl may be a bare origin OR carry a path
  // (e.g. ".../signin", the single_tenant.portal_redirect_url default) — either way, unless
  // it is ALREADY the success page, force the success path. (Only matching "" / "/" missed
  // the "/signin" case, which lands on the login buttons instead of DeviceCodeVerify.)
  let target = redirectTo;

  try {
    const urlObj = new URL(redirectTo);

    if (urlObj.pathname !== SUCCESS_SIGNIN_EXTERNAL_CLIENT_PATH) {
      urlObj.pathname = SUCCESS_SIGNIN_EXTERNAL_CLIENT_PATH;
      target = urlObj.toString();
    }
  } catch {
    // isQodoPortalHost already parsed it successfully, so this is unreachable — but if the
    // URL somehow doesn't parse, fall back to the original rather than throwing.
    return redirectTo;
  }

  // NB: user_code is NEVER carried across the redirect (not URL, not cookie). The user types
  // it manually on the ST device-confirm form (RFC 8628 / GitHub model) — that typed entry is
  // the possession proof + anti-phishing confirmation, and keeps the code out of URLs, browser
  // history, referer, and server logs. Only init_client/trace_id (non-secret) ride here.
  return buildURLWithQueryParams(
    target,
    buildSearchParams({
      // Preserve the in-flight client (cli or toolbox) across the ST redirect —
      // hardcoding cli would flip a toolbox login mid-flow.
      [QUERY_PARAMS.INIT_CLIENT]: getInitClientFromSessionStorage(),
      [QUERY_PARAMS.TRACE_ID]: getTraceIdFromSessionStorage(),
    }),
  );
};

/**
 * Clears the carried CLI device-login context once the device session is confirmed: the
 * adopted ST session_id and the code it was bound to (persisted by useAdoptDeviceCodeMutation
 * so a retry reuses the session for the same code but re-adopts a corrected one). Cleared on
 * success so a later login can't reuse a stale session/code binding.
 */
export const clearCarriedCliContext = (): void => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.SESSION_ID);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.USER_CODE);
};

/**
 * Gets the init_client from session storage. If not found, it checks the query params. If not found, returns 'portal'.
 * @returns The init_client value from session storage/query params or 'portal' as default
 */
export const getInitClientFromSessionStorage = (): string => {
  // The query param reads window.location.search and never throws; read it
  // outside the storage guard so the qodo-platform init_client redirect hint
  // (cli/vscode/…) is still honored when storage is blocked/disabled.
  const queryParamInitClient = getQueryParam(QUERY_PARAMS.INIT_CLIENT);

  // Called during render (SuccessPage decides whether to show the CLI flow);
  // sessionStorage can throw when storage is blocked/disabled — degrade
  // gracefully rather than crashing the page.
  try {
    const existingInitClient = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.INIT_CLIENT,
    );

    if (existingInitClient) {
      return existingInitClient;
    }

    if (queryParamInitClient) {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.INIT_CLIENT,
        queryParamInitClient,
      );
    }
  } catch {
    // Storage unavailable — fall through to the query-param hint / default.
  }

  return queryParamInitClient || 'portal';
};

/**
 * Gets the session_id from session storage. If not found, it checks the query params. If not found, generates a new one.
 * @returns The session_id value from session storage, query params, or a newly generated UUID
 */
export const getSessionIdFromSessionStorage = (): string | undefined => {
  const existingSessionId = sessionStorage.getItem(
    SESSION_STORAGE_KEYS.SESSION_ID,
  );
  const queryParamSessionId = getQueryParam(QUERY_PARAMS.SESSION_ID);

  if (existingSessionId) {
    return existingSessionId;
  }

  if (queryParamSessionId) {
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.SESSION_ID,
      queryParamSessionId,
    );

    return queryParamSessionId;
  }

  return undefined;
};

/**
 * Custom URLSearchParams that automatically filters out null, undefined, and empty string values
 * Arrays are converted to comma-separated values (e.g., ['active', 'inactive'] becomes 'active,inactive')
 * @param queryParams - Object with key-value pairs to include in the URLSearchParams
 * @returns URLSearchParams instance with only non-empty values
 */
export const buildSearchParams = (
  queryParams?: Record<
    string,
    string | number | boolean | string[] | null | undefined
  >,
): URLSearchParams => {
  if (!queryParams) return new URLSearchParams();

  const params = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (isNil(value) || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      const filteredValues = value.filter((item) => !!item);

      if (filteredValues.length > 0) {
        params.append(key, filteredValues.join(','));
      }
    } else {
      params.append(key, String(value));
    }
  });

  return params;
};

/**
 * Checks if the init_client is an IDE client (VSCode or JetBrains)
 */
const isIdeClient = (initClient: string | null): boolean => {
  return (
    initClient === CLIENT_TYPES.VSCODE || initClient === CLIENT_TYPES.JETBRAINS
  );
};

/**
 * Determines marketing parameters based on init_client
 * For IDE clients: uses init_client as source and 'qodo-gen' as type
 * For other clients: uses query params or defaults
 */
const determineMarketingParams = (
  initClient: string | null,
): { productSource: string; productType: string } => {
  if (isIdeClient(initClient)) {
    return {
      productSource: initClient!,
      productType: MARKETING_DEFAULTS.QODO_MERGE_PRODUCT_TYPE,
    };
  }

  const productSource = getQueryParam(MARKETING_PARAMS.PRODUCT_SOURCE);
  const productType = getQueryParam(MARKETING_PARAMS.PRODUCT_TYPE);

  return {
    productSource: productSource || MARKETING_DEFAULTS.PRODUCT_SOURCE,
    productType: productType || MARKETING_DEFAULTS.PRODUCT_TYPE,
  };
};

/**
 * Stores marketing parameters (productSource and productType) into localStorage
 * If init_client is vscode or jetbrains, stores productSource=init_client and productType=qodo-gen
 * Otherwise, stores values from URL query parameters or defaults
 */
export const storeMarketingParamsInLocalStorage = (): void => {
  const initClient = getQueryParam(QUERY_PARAMS.INIT_CLIENT);
  const { productSource, productType } = determineMarketingParams(initClient);

  localStorage.setItem(LOCAL_STORAGE_KEYS.PRODUCT_SOURCE, productSource);
  localStorage.setItem(LOCAL_STORAGE_KEYS.PRODUCT_TYPE, productType);
};

/**
 * Retrieves marketing parameters (productSource and productType) from session storage
 * @returns Object containing productSource and productType values, or null if not found
 */
export const getMarketingParamsFromLocalStorage = (): Record<
  string,
  string | null
> => {
  const productSource = localStorage.getItem(LOCAL_STORAGE_KEYS.PRODUCT_SOURCE);
  const productType = localStorage.getItem(LOCAL_STORAGE_KEYS.PRODUCT_TYPE);

  return { productSource, productType };
};

/**
 * Removes marketing parameters (productSource and productType) from local storage
 */
export const removeMarketingParamsFromLocalStorage = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.PRODUCT_SOURCE);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.PRODUCT_TYPE);
};

/**
 * Maps frontend column sorting to backend API sorting parameter
 * @param sorting - Array of sorting objects with 'id' and 'desc'
 * @param ColumnIdToApiField - Optional mapping of column IDs to API field names
 * @param skipDescPrefix - If true, skips adding the '-' prefix for descending sort
 * @returns API sorting parameter string (e.g., '-last_login')
 */
export const getApiSortingParam = (
  sorting: { id: string; desc: boolean }[],
  ColumnIdToApiField: Record<string, string> = {},
  skipDescPrefix: boolean = false,
): string | undefined => {
  if (!sorting || sorting.length === 0) return undefined;

  const { id, desc } = sorting[0];
  const apiField = ColumnIdToApiField[id] || id;

  return `${desc && !skipDescPrefix ? '-' : ''}${apiField}`;
};

/**
 * Validates if a string is a valid HTTP/HTTPS URL.
 * Ensures the URL can be safely parsed and uses a secure protocol.
 * This is used as a security measure to prevent redirects to invalid or malicious URLs.
 * @param url - The URL string to validate
 * @returns true if the URL is valid and uses http: or https: protocol, false otherwise
 * @example
 * isValidRedirectUrl('https://example.com') // true
 * isValidRedirectUrl('ftp://example.com') // false
 * isValidRedirectUrl('javascript:alert(1)') // false
 * isValidRedirectUrl('not-a-url') // false
 */
export const isValidRedirectUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);

    return VALID_REDIRECT_PROTOCOLS.has(parsed.protocol);
  } catch {
    console.warn(
      'Failed to parse url in isValidRedirectUrl. Returning default false',
    );

    return false;
  }
};

/**
 * Returns true if the given URL has a different origin than the current page.
 * Used to determine whether a 409 redirect crosses origins (and therefore
 * requires a cookie-based token handoff).
 * @param url - The redirect URL to check
 */
export const isCrossOriginUrl = (url: string): boolean => {
  try {
    return new URL(url).origin !== window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Checks if redirecting to a URL would create a redirect loop.
 * Compares the target URL with the last redirect that occurred within a time window.
 * This prevents infinite redirect loops that could crash the browser or create poor UX.
 * @param url - The URL to check for potential loop
 * @returns true if redirecting would create a loop (same URL redirected within timeout window), false otherwise
 * @example
 * // First redirect to /page - no loop detected
 * isRedirectLoop('/page') // false
 * trackRedirect('/page')
 * // Immediate redirect to same URL - loop detected
 * isRedirectLoop('/page') // true
 * // After timeout, same URL is allowed again
 * setTimeout(() => isRedirectLoop('/page'), 6000) // false
 */
export const isRedirectLoop = (url: string): boolean => {
  const key = SESSION_STORAGE_KEYS.LAST_REDIRECT;

  try {
    // getItem is inside the try: sessionStorage can throw when blocked/disabled, and this
    // runs in the 409 redirect path (now also reachable from the external-client success
    // page) — a throw must not abort the redirect. No stored marker => not a loop.
    const stored = sessionStorage.getItem(key);

    if (!stored) {
      return false;
    }

    const { url: lastUrl, timestamp } = JSON.parse(stored);
    const now = Date.now();
    const timeDiff = now - timestamp;

    // If same URL within the timeout window, it's a loop
    return lastUrl === url && timeDiff < REDIRECT_LOOP_TIMEOUT_MS;
  } catch {
    // Invalid stored data or storage unavailable — best-effort: not a detectable loop.
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* storage unavailable — nothing to clear */
    }

    return false;
  }
};

/**
 * Validates if a URL is a valid GitHub URL.
 * Checks that the URL uses http/https protocol and has a GitHub hostname.
 * Accepts both github.com and GitHub Enterprise URLs (github.yourcompany.com).
 *
 * @param url - A parsed URL object to validate
 * @returns True if the URL is a valid GitHub URL, false otherwise
 *
 * @example
 * ```ts
 * const url1 = new URL('https://github.com/acme-corp');
 * isValidGithubUrl(url1); // Returns: true
 *
 * const url2 = new URL('https://github.enterprise.com/acme-corp');
 * isValidGithubUrl(url2); // Returns: true
 *
 * const url3 = new URL('https://gitlab.com/acme-corp');
 * isValidGithubUrl(url3); // Returns: false
 *
 * const url4 = new URL('https://notgithub.com/acme-corp');
 * isValidGithubUrl(url4); // Returns: false
 * ```
 */
export const isValidGithubUrl = (url: URL): boolean => {
  const isValidProtocol = url.protocol === 'http:' || url.protocol === 'https:';
  const isGithubHostname =
    url.hostname === 'github.com' || url.hostname.startsWith('github.');

  return isValidProtocol && isGithubHostname;
};
